import { Col, Row } from 'react-grid-system'
import cx from 'classnames'
import styles from './CardGroup.module.scss'
import { getResponsiveCarouselSettings } from 'utils/carousel/getResponsiveCarouselSettings'
import { useWindowSize } from 'utils/hooks'
import { Card, CarouselV2, Heading } from '~elements'
import { CardBackgroundColorType, CardType, EyeBrowColorType, FontSizeType, GradientType, HeadlineType } from '~types'
import { camelToKebab } from 'utils/styles/camelToKebab'

type Props = {
  backgroundGradient?: GradientType
  backgroundColor?: CardBackgroundColorType
  cards?: CardType[]
  centerCopyOnMobile?: boolean
  headline?: HeadlineType
  isCarousel?: boolean
  isCarouselMobileOnly?: boolean
  stackMediaAndCopy?: boolean
  hasDescriptionListCheckMark?: boolean
  subheadlineColor?: EyeBrowColorType
  slidesToShow?: number
  cardsHeaderFontSize?: 'large' | 'small'
  cardsDescriptionFontSize?: 'lg' | 'mdPlus' | 'md' | 'sm' | 'xs'
}

const CardGroup = ({
  backgroundGradient,
  backgroundColor,
  cardsHeaderFontSize = 'large',
  cardsDescriptionFontSize = 'mdPlus',
  cards,
  centerCopyOnMobile,
  hasDescriptionListCheckMark,
  headline,
  isCarousel,
  isCarouselMobileOnly,
  stackMediaAndCopy,
  subheadlineColor,
  slidesToShow = 3
}: Props) => {
  const { isBreakpoint: isBelowMd } = useWindowSize('sm', '<')
  const hasOneItemAndStackMedia = stackMediaAndCopy && cards?.length === 1
  const itemsLength = hasOneItemAndStackMedia ? 3 : cards?.length
  const colSpan = Math.max(12 / Math.min(itemsLength ?? 1, 3), 3)
  const hasHeadlineTitle = headline && headline.title && !isCarousel
  const normalizedDescriptionFontSize = camelToKebab(cardsDescriptionFontSize) as FontSizeType
  const shouldShowAsCarousel = (isCarousel && !isCarouselMobileOnly) || (isCarouselMobileOnly && isBelowMd)
  const responsive = getResponsiveCarouselSettings(slidesToShow, {
    sm: {
      dots: true
    }
  })

  return (
    <div
      className={cx(styles['card-group'], styles[`card-group__header--${cardsHeaderFontSize}`])}
      style={{
        ...(hasHeadlineTitle && { padding: '32px 24px' })
      }}
    >
      {hasHeadlineTitle && (
        <div
          className={cx(
            styles[`card-group__headline`],
            headline.justify !== 'center' && styles[`card-group__headline-padding`]
          )}
        >
          <Heading level={headline.level} justify={headline.justify} levelDisplay={headline.levelDisplay}>
            {headline.title}
          </Heading>
        </div>
      )}
      <Row
        style={{
          margin: '0',
          ...(shouldShowAsCarousel && { display: 'block' }),
          ...(!hasHeadlineTitle && { padding: '32px 24px' }),
          ...(isBelowMd && { gap: '32px' })
        }}
      >
        {shouldShowAsCarousel ? (
          <CarouselV2
            slides={cards ?? []}
            showArrows
            alwaysShowArrows
            headline={headline}
            carouselSettings={{
              variableWidth: false,
              slidesToShow: 3,
              responsive
            }}
            renderSlide={slide => (
              <Card
                key={slide.id}
                {...slide}
                backgroundColor={backgroundColor}
                backgroundGradient={backgroundGradient}
                stackMediaAndCopy={stackMediaAndCopy}
                centerCopyOnMobile={centerCopyOnMobile}
                hasDescriptionListCheckMark={hasDescriptionListCheckMark}
                cardsDescriptionFontSize={normalizedDescriptionFontSize}
                subheadlineColor={subheadlineColor}
              />
            )}
          />
        ) : (
          <>
            {cards && cards.length > 0 && (
              <>
                {cards.map(card => (
                  <Col
                    key={card.id}
                    md={Math.floor(colSpan)}
                    style={{
                      marginTop: '16px',
                      ...(hasOneItemAndStackMedia && { margin: '0 auto' })
                    }}
                  >
                    <Card
                      {...card}
                      backgroundColor={backgroundColor}
                      backgroundGradient={backgroundGradient}
                      stackMediaAndCopy={stackMediaAndCopy}
                      centerCopyOnMobile={centerCopyOnMobile}
                      hasDescriptionListCheckMark={hasDescriptionListCheckMark}
                      cardsDescriptionFontSize={normalizedDescriptionFontSize}
                      subheadlineColor={subheadlineColor}
                    />
                  </Col>
                ))}
              </>
            )}
          </>
        )}
      </Row>
    </div>
  )
}

export default CardGroup
